// ── AccountStore — Zustand store for Kalshi account state (MT relay) ──
//
// In the MT world, all Kalshi operations go through the relay API.
// Keys are stored server-side. The browser never signs requests directly.
// This store preserves the same state shape so views don't change.

import { create } from 'zustand';
import { apiFetch } from './authStore';
import type {
  KalshiPosition,
  KalshiFill,
  KalshiOrder,
  OrderInput,
  KeyHealth,
  AccountStatus,
} from '../types/kalshi';
import { readSoundSettings } from '../lib/audio/soundSettings';
import { playFillTone, playKaChingTone, playWhaleTone, playOrderRejectedTone, playOrderPlacedTone } from '../lib/audio/tradeAudio';
import { getAudioContext, setPendingSound } from '../lib/audio/audioContext';

const POLL_INTERVAL_MS = 5000;
const MAX_SEEN_FILLS = 1000;
const MAX_RECENT_FILLS = 100;
const ACTIVITY_TICKER_SIZE = 20;

let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _connectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let _connectRetryAttempts = 0;
let _connectionGeneration = 0;
const MAX_CONNECT_RETRIES = 5;
const BASE_CONNECT_RETRY_MS = 3_000;

const _seenFillIds = new Set<string>();
const _seenFillOrder: string[] = [];
const _seenOrderIds = new Set<string>();

interface AccountState {
  status: AccountStatus;
  error: string | null;
  keyHealth: KeyHealth[];
  credentialCount: number;
  keyMode: 'org' | 'user' | null;

  balance: number | null;
  positions: KalshiPosition[];
  openOrders: KalshiOrder[];
  recentFills: KalshiFill[];
  activityTicker: KalshiFill[];
  realizedPnlCents: number;
  openNotionalCents: number;

  connect: () => Promise<void>;
  disconnect: () => void;
  refreshPositions: () => Promise<void>;
  refreshOrders: () => Promise<void>;
  refreshPnl: () => Promise<void>;
  processFill: (fill: KalshiFill) => void;
  placeOrder: (input: OrderInput) => Promise<KalshiOrder>;
  cancelOrder: (orderId: string) => Promise<void>;
  checkCredentials: () => Promise<void>;
}

function stopPolling() {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}

function startPolling(poll: () => void) {
  stopPolling();
  poll();
  _pollTimer = setInterval(poll, POLL_INTERVAL_MS);
}

function nextConnectionGeneration() {
  _connectionGeneration += 1;
  return _connectionGeneration;
}

function isCurrentConnectionGeneration(generation: number) {
  return _connectionGeneration === generation;
}

function pickStr(...c: unknown[]) { for (const v of c) if (typeof v === 'string' && v !== '') return v; return undefined; }
function pickNum(...c: unknown[]) { for (const v of c) { if (typeof v === 'number' && !isNaN(v)) return v; if (typeof v === 'string' && v !== '') { const n = parseFloat(v); if (!isNaN(n)) return n; } } return undefined; }

function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number {
  if (typeof dollars === 'string' && dollars !== '') { const n = parseFloat(dollars); if (!isNaN(n)) return Math.round(n * 100); }
  if (typeof dollars === 'number') return dollars <= 1.01 ? Math.round(dollars * 100) : Math.round(dollars);
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents);
  return 0;
}

function parsePositions(data: Record<string, unknown>): KalshiPosition[] {
  const raw = (data.market_positions ?? data.positions ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((p): KalshiPosition | null => {
    const ticker = pickStr(p.ticker, p.market_ticker);
    if (!ticker) return null;

    // V2 shape from /api/trader/positions: { ticker, side, netCount, avgEntryPriceCents, realizedPnlCents }
    if ('netCount' in p) {
      const position = pickNum(p.netCount) ?? 0;
      if (position <= 0) return null;
      return {
        ticker,
        side: (p.side as 'yes' | 'no') ?? 'yes',
        position,
        averagePrice: pickNum(p.avgEntryPriceCents) ?? 0,
        realizedPnl: pickNum(p.realizedPnlCents) ?? 0,
      };
    }

    const rawPos = pickNum(p.position);
    let side: 'yes' | 'no';
    let position: number;
    if (rawPos !== undefined) { side = rawPos >= 0 ? 'yes' : 'no'; position = Math.abs(rawPos); }
    else { side = (p.side as 'yes' | 'no') ?? 'yes'; position = pickNum(p.quantity, p.total_traded) ?? 0; }
    if (position === 0) return null;
    const avgPrice = dollarsToCents(p.average_price_dollars ?? p[`${side}_price_dollars`], p.average_price ?? p[`${side}_price`]);
    const realizedPnl = dollarsToCents(p.realized_pnl_dollars, p.realized_pnl);
    return { ticker, side, position, averagePrice: avgPrice, realizedPnl };
  }).filter((p): p is KalshiPosition => p !== null);
}

function positionsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/positions' : '/api/kalshi/positions';
}

// Org-scope fills come from the relay's persisted fills table (poller-resolved
// user_id), not the live Kalshi fetch — the latter filters by trades.kalshi_order_id
// which silently drops any fill whose order didn't get logged with a non-null
// kalshiOrderId, which has been masking recent fills entirely in org mode.
function fillsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/fills' : '/api/kalshi/fills';
}

function parseOrders(data: Record<string, unknown>): KalshiOrder[] {
  const raw = (data.orders ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((o): KalshiOrder => {
    const side = (o.side as 'yes' | 'no') ?? 'yes';
    return {
      orderId: pickStr(o.order_id, o.id) ?? '',
      ticker: pickStr(o.ticker, o.market_ticker) ?? '',
      side,
      action: (o.action as 'buy' | 'sell') ?? 'buy',
      type: (o.type as 'limit' | 'market') ?? 'limit',
      status: pickStr(o.status) ?? 'resting',
      count: pickNum(o.count, o.count_fp, o.quantity) ?? 0,
      remainingCount: pickNum(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.quantity) ?? 0,
      yesPrice: dollarsToCents(o.yes_price_dollars, o.yes_price) || undefined,
      noPrice: dollarsToCents(o.no_price_dollars, o.no_price) || undefined,
      createdTime: pickStr(o.created_time, o.created_at) ?? '',
    };
  });
}

function parseFills(data: Record<string, unknown>): KalshiFill[] {
  const raw = (data.fills ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((f): KalshiFill => {
    const side = (f.side as 'yes' | 'no') ?? 'yes';
    const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
    return {
      fillId: pickStr(f.fill_id, f.trade_id, f.id) ?? `fill-${Date.now()}`,
      orderId: pickStr(f.order_id) ?? '',
      ticker: pickStr(f.ticker, f.market_ticker) ?? '',
      side,
      action: (f.action as 'buy' | 'sell') ?? 'buy',
      count: pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0,
      price,
      createdAt: pickStr(f.created_at, f.created_time) ?? new Date().toISOString(),
    };
  });
}

export const useAccountStore = create<AccountState>((set, get) => ({
  status: 'disconnected',
  error: null,
  keyHealth: [],
  credentialCount: 0,
  keyMode: null,
  balance: null,
  positions: [],
  openOrders: [],
  recentFills: [],
  activityTicker: [],
  realizedPnlCents: 0,
  openNotionalCents: 0,

  checkCredentials: async () => {
    try {
      const res = await apiFetch('/api/trader/key-mode');
      if (!res.ok) { set({ credentialCount: 0 }); return; }
      const data = await res.json() as { canTrade?: boolean; mode?: 'org' | 'user' };
      const canTrade = data.canTrade === true;
      const keyMode: 'org' | 'user' | null = data.mode === 'org' || data.mode === 'user' ? data.mode : null;
      set({ credentialCount: canTrade ? 1 : 0, keyMode });
      const s = get().status;
      if (canTrade && (s === 'disconnected' || s === 'error')) {
        get().connect();
      }
    } catch {
      set({ credentialCount: 0 });
    }
  },

  connect: async () => {
    if (get().status === 'connecting') return;
    const generation = nextConnectionGeneration();
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    set({ status: 'connecting', error: null });

    try {
      const modeRes = await apiFetch('/api/trader/key-mode');
      if (!modeRes.ok) {
        const errData = await modeRes.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error ?? 'Failed to load key mode');
      }
      const modeData = await modeRes.json() as { canTrade?: boolean; mode?: 'org' | 'user' };
      if (!isCurrentConnectionGeneration(generation)) return;
      const canTrade = modeData.canTrade === true;
      const keyMode: 'org' | 'user' = modeData.mode === 'org' ? 'org' : 'user';
      set({ credentialCount: canTrade ? 1 : 0, keyMode });

      if (!canTrade) {
        if (!isCurrentConnectionGeneration(generation)) return;
        _connectRetryAttempts = 0;
        set({ status: 'disconnected', error: 'No credentials available. Ask your manager to add org keys, or add personal keys in Settings.' });
        return;
      }

      const balRes = await apiFetch('/api/kalshi/balance');
      if (!isCurrentConnectionGeneration(generation)) return;
      if (!balRes.ok) {
        const errData = await balRes.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error ?? 'Failed to fetch balance');
      }
      const balData = await balRes.json() as Record<string, unknown>;
      if (!isCurrentConnectionGeneration(generation)) return;
      // In org-keys mode the balance endpoint returns a placeholder — leave balance null
      // so the UI hides the value instead of showing "$0.00".
      const balance = balData.scope === 'org'
        ? null
        : pickNum(balData.balance, balData.available_balance) ?? 0;

      const [posRes, ordRes, pnlRes] = await Promise.all([
        apiFetch(positionsEndpointFor(keyMode)),
        apiFetch('/api/kalshi/orders'),
        keyMode === 'org' ? apiFetch('/api/trader/pnl') : Promise.resolve(null),
      ]);
      if (!isCurrentConnectionGeneration(generation)) return;

      const positions = posRes.ok ? parsePositions(await posRes.json()) : [];
      const orders = ordRes.ok ? parseOrders(await ordRes.json()) : [];
      const pnl = pnlRes && pnlRes.ok
        ? await pnlRes.json() as { realizedPnlCents?: number; openNotionalCents?: number }
        : null;
      if (!isCurrentConnectionGeneration(generation)) return;

      _connectRetryAttempts = 0;
      set({
        status: 'connected', error: null, balance,
        positions, openOrders: orders,
        realizedPnlCents: pnl?.realizedPnlCents ?? 0,
        openNotionalCents: pnl?.openNotionalCents ?? 0,
      });

      startPolling(() => {
        const s = get().status;
        if (!isCurrentConnectionGeneration(generation) || s !== 'connected') {
          stopPolling();
          return;
        }

        apiFetch('/api/kalshi/balance').then(async (r) => {
          if (r.ok) {
            const d = await r.json() as Record<string, unknown>;
            if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
            // Org-scope placeholder leaves balance untouched at null.
            if (d.scope === 'org') return;
            set({ balance: pickNum(d.balance, d.available_balance) ?? get().balance });
          }
        }).catch(() => {});

        get().refreshPositions().catch(() => {});
        get().refreshOrders().catch(() => {});
        get().refreshPnl().catch(() => {});

        apiFetch(fillsEndpointFor(get().keyMode)).then(async (r) => {
          if (r.ok) {
            const fills = parseFills(await r.json());
            if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
            for (const fill of fills) get().processFill(fill);
          }
        }).catch(() => {});
      });
    } catch (err) {
      if (!isCurrentConnectionGeneration(generation)) return;
      const message = err instanceof Error ? err.message : 'Connection failed';
      stopPolling();
      if (_connectRetryAttempts < MAX_CONNECT_RETRIES) {
        const delay = BASE_CONNECT_RETRY_MS * 2 ** _connectRetryAttempts;
        _connectRetryAttempts++;
        set({ status: 'connecting', error: `${message} — retrying in ${Math.round(delay / 1000)}s (${_connectRetryAttempts}/${MAX_CONNECT_RETRIES})` });
        _connectRetryTimer = setTimeout(() => {
          if (!isCurrentConnectionGeneration(generation)) return;
          void get().connect();
        }, delay);
      } else {
        _connectRetryAttempts = 0;
        set({ status: 'error', error: message });
      }
    }
  },

  disconnect: () => {
    _connectionGeneration += 1;
    stopPolling();
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    _connectRetryAttempts = 0;
    _seenFillIds.clear();
    _seenFillOrder.length = 0;
    _seenOrderIds.clear();
    set({
      status: 'disconnected',
      error: null,
      keyHealth: [],
      keyMode: null,
      balance: null,
      positions: [],
      openOrders: [],
      recentFills: [],
      activityTicker: [],
      realizedPnlCents: 0,
      openNotionalCents: 0,
    });
  },

  refreshPositions: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected') return;
    try {
      const res = await apiFetch(positionsEndpointFor(get().keyMode));
      if (!res.ok) return;
      const positions = parsePositions(await res.json());
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      set({ positions });
    } catch {}
  },

  refreshPnl: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected' || get().keyMode !== 'org') return;
    try {
      const res = await apiFetch('/api/trader/pnl');
      if (!res.ok) return;
      const pnl = await res.json() as { realizedPnlCents?: number; openNotionalCents?: number };
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      set({
        realizedPnlCents: pnl.realizedPnlCents ?? 0,
        openNotionalCents: pnl.openNotionalCents ?? 0,
      });
    } catch {}
  },

  refreshOrders: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected') return;
    try {
      const res = await apiFetch('/api/kalshi/orders');
      if (!res.ok) return;
      const orders = parseOrders(await res.json());
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      set({ openOrders: orders });
    } catch {}
  },

  processFill: (fill: KalshiFill) => {
    if (_seenFillIds.has(fill.fillId)) return;
    _seenFillIds.add(fill.fillId);
    _seenFillOrder.push(fill.fillId);
    while (_seenFillIds.size > MAX_SEEN_FILLS) {
      const oldest = _seenFillOrder.shift();
      if (oldest) _seenFillIds.delete(oldest);
    }

    const recentFills = [fill, ...get().recentFills].slice(0, MAX_RECENT_FILLS);
    const activityTicker = recentFills.slice(0, ACTIVITY_TICKER_SIZE);
    set({ recentFills, activityTicker });

    const fillAgeMs = Date.now() - new Date(fill.createdAt).getTime();
    const isRecent = fillAgeMs < 10_000;
    const snd = readSoundSettings();
    if (isRecent && snd.tradeSoundEnabled && fill.count >= snd.minFillContracts) {
      const isFirstFill = !_seenOrderIds.has(fill.orderId);
      _seenOrderIds.add(fill.orderId);

      const playFn = fill.count >= 1000 && snd.whaleSoundEnabled ? playWhaleTone
        : isFirstFill && snd.firstSaleSoundEnabled ? playKaChingTone
        : playFillTone;

      const ctx = getAudioContext();
      if (ctx?.state === 'running') { playFn(); }
      else { setPendingSound(playFn); }
    }

    get().refreshPositions().catch(() => {});
  },

  placeOrder: async (input: OrderInput) => {
    const body: Record<string, unknown> = {
      ticker: input.ticker,
      side: input.side,
      action: input.action,
      type: input.type,
      count: input.count,
      count_fp: String(input.count),
    };
    if (input.price !== undefined) {
      body[`${input.side}_price_dollars`] = (input.price / 100).toFixed(2);
    }
    if (input.postOnly) body.post_only = true;
    if (input.timeInForce) body.time_in_force = input.timeInForce;
    if (input.expirationTime) body.expiration_ts = Math.floor(new Date(input.expirationTime).getTime() / 1000);

    try {
      const res = await apiFetch('/api/kalshi/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error ?? `Order failed (${res.status})`);
      }
      const data = await res.json() as Record<string, unknown>;
      const order = (data.order ?? data) as Record<string, unknown>;

      const snd = readSoundSettings();
      if (snd.orderPlacedSoundEnabled) playOrderPlacedTone();

      get().refreshOrders().catch(() => {});

      return {
        orderId: pickStr(order.order_id, order.id) ?? '',
        ticker: input.ticker,
        side: input.side,
        action: input.action,
        type: input.type,
        status: pickStr(order.status) ?? 'resting',
        count: input.count,
        remainingCount: pickNum(order.remaining_count, order.remaining_count_fp) ?? input.count,
        yesPrice: input.side === 'yes' ? input.price : undefined,
        noPrice: input.side === 'no' ? input.price : undefined,
        createdTime: pickStr(order.created_time) ?? new Date().toISOString(),
      } as KalshiOrder;
    } catch (err) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone();
      throw err;
    }
  },

  cancelOrder: async (orderId: string) => {
    const res = await apiFetch(`/api/kalshi/orders/${orderId}`, { method: 'DELETE' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error((errData as Record<string, string>).error ?? `Cancel failed (${res.status})`);
    }
    get().refreshOrders().catch(() => {});
  },
}));
